import React, { useState, useEffect } from "react";
import { X } from "@phosphor-icons/react";

const STORAGE_PREFIX = "pronto:intro-dismissed:";

/**
 * Riapre una specifica guida dismessa (rimuove la chiave dal localStorage).
 * L'utente al prossimo render vedrà di nuovo il banner.
 *
 * Uso: `resetDismissedIntro("fotoeditor:intro")` — chiamato dal pulsante "?".
 */
export function resetDismissedIntro(storageKey) {
  try {
    localStorage.removeItem(`${STORAGE_PREFIX}${storageKey}`);
  } catch { /* localStorage non disponibile */ }
}

/**
 * Riapre TUTTE le guide dismesse (Profilo > Riapri tutte le guide).
 * Scansiona le chiavi del localStorage con il prefisso `pronto:intro-dismissed:`.
 */
export function resetAllDismissedIntros() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(STORAGE_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
    return keys.length;
  } catch {
    return 0;
  }
}

/**
 * DismissibleIntro — banner introduttivo che l'utente può chiudere con X.
 * Lo stato chiuso è persistente in localStorage con la `storageKey` data.
 *
 * Uso tipico: descrizioni "Ciao, sono…" / "Entri in casa, parli e scatti…"
 * che servono al primo accesso ma diventano rumore una volta che l'agente
 * sa muoversi nell'app.
 *
 * Props:
 *   - storageKey: chiave univoca per persistenza (es. "intro:assistente")
 *   - children:   contenuto del banner
 *   - className:  classi extra opzionali per il wrapper
 *   - align:      "center" (default) | "left" — sui mobile è meglio
 *                 il centrato per coerenza visiva (iter v29 r245 User M1399);
 *                 le card con contenuto strutturato (es. bullet list)
 *                 possono opt-out con align="left".
 *
 * Se l'utente ha già chiuso il banner in passato → non viene renderizzato.
 *
 * iter 305 round 9: la guida può essere riaperta via `resetDismissedIntro(storageKey)`
 * o globalmente via `resetAllDismissedIntros()`. Il componente ascolta l'evento
 * custom `pronto:intros-reset` per ri-mostrarsi senza bisogno di refresh.
 */
export default function DismissibleIntro({
  storageKey,
  children,
  className = "",
  align = "center",
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const check = () => {
      try {
        const dismissed = localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`);
        setVisible(!dismissed);
      } catch {
        setVisible(true);
      }
    };
    check();
    const onReset = () => check();
    window.addEventListener("pronto:intros-reset", onReset);
    // iter v28 r363 — User M1273 (mobile bug): su iOS Safari quando una
    // guida è nascosta (return null) ma viene riaperta da un altro evento,
    // alcune versioni del browser non triggerano subito i re-render.
    // Ascoltiamo anche `storage` (cross-tab) e `visibilitychange` (ritorno
    // alla scheda dopo aver minimizzato) per ri-controllare.
    const onStorage = (e) => {
      if (!e || !e.key || e.key.startsWith(STORAGE_PREFIX)) check();
    };
    const onVisibility = () => { if (!document.hidden) check(); };
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pronto:intros-reset", onReset);
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [storageKey]);

  const close = () => {
    setVisible(false);
    try {
      localStorage.setItem(`${STORAGE_PREFIX}${storageKey}`, "1");
    } catch {
      /* localStorage piena/disabilitata → chiudi solo per la sessione */
    }
  };

  if (!visible) return null;

  // iter v30 r092 — User feedback: la X cadeva FUORI dal box grigio perché
  // il wrapper aveva pr-9 (padding-right) e la X era posizionata "right-1"
  // sul wrapper esterno. Ora wrapper senza padding extra e X dentro la card,
  // in alto a destra, con z-index per stare sopra al contenuto.
  return (
    <div
      className={`relative ${align === "center" ? "text-center" : ""} ${className}`}
      data-testid={`intro-${storageKey}`}
    >
      {children}
      <button
        type="button"
        onClick={close}
        className="absolute top-2 right-2 sm:top-3 sm:right-3 w-7 h-7 rounded-full bg-white/70 backdrop-blur-sm text-[#6B6A65] hover:text-[#1C1C1A] hover:bg-[#EDEAE2] inline-flex items-center justify-center transition-colors z-10"
        aria-label="Nascondi spiegazione"
        title="Nascondi spiegazione · puoi riaprirla con il '?' in alto"
        data-testid={`intro-${storageKey}-close`}
      >
        <X size={13} weight="bold" />
      </button>
    </div>
  );
}
